import {
  ACCESS_PERMISSION_KEYS,
  buildAccessState,
  isBillingWorkspaceOnly,
  resolveBillingWorkspaceEntryPath,
} from "./accessControl.js";
import { writeAccessSessionCache } from "./accessSessionCache.js";
import { fetchWithSupabaseAuth } from "./supabaseClient.js";

export const DRIVER_PORTAL_HOME_PATH = "/portail";
export const DRIVER_PORTAL_DELIVERY_PREFIX = "/portail/livraisons";
export const OPERATOR_HOME_PATH = "/accueil";
export const LEGACY_DRIVER_PORTAL_HOME_PATH = "/driver";
export const LEGACY_DRIVER_PORTAL_DELIVERY_PREFIX = "/driver/deliveries";
export const DRIVER_PORTAL_ROUTE_UPDATE_PENDING_CODE = "driver_portal_route_update_pending";
export const DRIVER_PORTAL_ROUTE_UPDATE_PENDING_MESSAGE = "Les commandes de votre camion ont été modifiées depuis la dernière publication.";
export const DRIVER_PORTAL_PUBLICATION_WITHDRAWN_CODE = "driver_portal_publication_withdrawn";
export const DRIVER_PORTAL_PUBLICATION_WITHDRAWN_MESSAGE = "Cette journée a été retirée du portail chauffeur.";

const DRIVER_PORTAL_DISPATCH_DATE_FORMATTER = new Intl.DateTimeFormat("fr-CA", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function normalizeDispatchDate(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  return /^\d{4}-\d{2}-\d{2}$/.test(normalizedValue) ? normalizedValue : "";
}

function normalizeDispatchPeriod(value, { allowEmpty = false } = {}) {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalizedValue === "evening" || normalizedValue === "soir") {
    return "evening";
  }
  if (normalizedValue === "day" || normalizedValue === "jour") {
    return "day";
  }
  return allowEmpty ? "" : "day";
}

function normalizePublicationRevision(value) {
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : null;
}

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function collectDriverPortalExecutionRevisions(payload) {
  const deliveryExecutionRevisions = {};
  const loadingExecutionRevisions = {};
  const visit = (value) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!value || typeof value !== "object") {
      return;
    }
    const executionRevision = normalizeInlineText(value.execution_revision ?? value.executionRevision);
    const deliveryId = normalizeInlineText(value.delivery_id ?? value.deliveryId);
    const loadingStepId = normalizeInlineText(value.loading_step_id ?? value.loadingStepId ?? (
      value.step_type === "loading" ? value.step_id : ""
    ));
    if (executionRevision && value.step_type === "loading" && loadingStepId) {
      loadingExecutionRevisions[loadingStepId] = executionRevision;
    } else if (executionRevision && deliveryId) {
      deliveryExecutionRevisions[deliveryId] = executionRevision;
    }
    Object.values(value).forEach(visit);
  };
  visit(payload);
  return { deliveryExecutionRevisions, loadingExecutionRevisions };
}

function unwrapDriverPortalPublicationUnavailability(value) {
  if (!value || typeof value !== "object") {
    return null;
  }

  if (value.publicationUnavailability && typeof value.publicationUnavailability === "object") {
    return value.publicationUnavailability;
  }

  if (value.publication_unavailability && typeof value.publication_unavailability === "object") {
    return value.publication_unavailability;
  }

  const detail = value.detail && typeof value.detail === "object" ? value.detail : null;
  if (detail?.publication_unavailability && typeof detail.publication_unavailability === "object") {
    return detail.publication_unavailability;
  }
  if (detail) {
    return detail;
  }

  const payload = value.payload && typeof value.payload === "object" ? value.payload : null;
  if (payload?.detail?.publication_unavailability && typeof payload.detail.publication_unavailability === "object") {
    return payload.detail.publication_unavailability;
  }
  if (payload?.detail && typeof payload.detail === "object") {
    return payload.detail;
  }
  if (payload?.publication_unavailability && typeof payload.publication_unavailability === "object") {
    return payload.publication_unavailability;
  }

  return value;
}

export function normalizeDriverPortalPublicationUnavailability(value) {
  const source = unwrapDriverPortalPublicationUnavailability(value);
  const code = normalizeInlineText(source?.code);
  if (code !== DRIVER_PORTAL_ROUTE_UPDATE_PENDING_CODE && code !== DRIVER_PORTAL_PUBLICATION_WITHDRAWN_CODE) {
    return null;
  }
  const defaultMessage = code === DRIVER_PORTAL_PUBLICATION_WITHDRAWN_CODE
    ? DRIVER_PORTAL_PUBLICATION_WITHDRAWN_MESSAGE
    : DRIVER_PORTAL_ROUTE_UPDATE_PENDING_MESSAGE;

  return {
    code,
    dispatchDate: normalizeDispatchDate(source.dispatch_date || source.dispatchDate),
    dispatchPeriod: normalizeDispatchPeriod(source.dispatch_period || source.dispatchPeriod, { allowEmpty: true }),
    message: normalizeInlineText(source.message) || defaultMessage,
    status: normalizeInlineText(source.status),
    withdrawalReason: normalizeInlineText(source.withdrawal_reason || source.withdrawalReason),
    withdrawnAt: normalizeInlineText(source.withdrawn_at || source.withdrawnAt),
  };
}

function parseDriverPortalDispatchDate(value) {
  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedValue);
  if (dateMatch) {
    const year = Number(dateMatch[1]);
    const month = Number(dateMatch[2]);
    const day = Number(dateMatch[3]);

    if ([year, month, day].every(Number.isInteger)) {
      return new Date(year, month - 1, day, 12);
    }
  }

  const parsedDate = new Date(normalizedValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function formatDriverPortalDispatchDateLabel(value) {
  if (!value) {
    return "Aujourd'hui";
  }

  const parsedDate = parseDriverPortalDispatchDate(value);
  if (!parsedDate) {
    return "Aujourd'hui";
  }

  const formattedParts = DRIVER_PORTAL_DISPATCH_DATE_FORMATTER.formatToParts(parsedDate);
  const day = formattedParts.find((part) => part.type === "day")?.value || "";
  const month = formattedParts.find((part) => part.type === "month")?.value || "";
  const year = formattedParts.find((part) => part.type === "year")?.value || "";
  const truncatedMonth = month.length > 4 ? month.slice(0, 4) : month;

  return [day, truncatedMonth, year].filter(Boolean).join(" ");
}

function normalizePreviewDriverUserId(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function resolveDriverPortalPathOptions(dispatchDateOrOptions = "", previewDriverUserId = "") {
  if (dispatchDateOrOptions && typeof dispatchDateOrOptions === "object") {
    return {
      dispatchDate: normalizeDispatchDate(dispatchDateOrOptions.dispatchDate),
      dispatchPeriod: normalizeDispatchPeriod(dispatchDateOrOptions.dispatchPeriod, { allowEmpty: true }),
      previewDriverUserId: normalizePreviewDriverUserId(dispatchDateOrOptions.previewDriverUserId),
    };
  }

  return {
    dispatchDate: normalizeDispatchDate(dispatchDateOrOptions),
    dispatchPeriod: "",
    previewDriverUserId: normalizePreviewDriverUserId(previewDriverUserId),
  };
}

export function readDriverDispatchDateFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeDispatchDate(params.get("dispatch_date"));
}

export function readDriverDispatchPeriodFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeDispatchPeriod(params.get("dispatch_period"), { allowEmpty: true });
}

export function readDriverPreviewDriverUserIdFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizePreviewDriverUserId(params.get("preview_driver_user_id"));
}

function appendDriverPortalSearchParams(path, {
  dispatchDate = "",
  dispatchPeriod = "",
  previewDriverUserId = "",
} = {}) {
  if (typeof path !== "string" || !path.trim()) {
    return path;
  }

  const resolvedUrl = new URL(path, "https://grain.local");
  const resolvedDispatchDate = normalizeDispatchDate(dispatchDate);
  const resolvedDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod, { allowEmpty: true });
  const resolvedPreviewDriverUserId = normalizePreviewDriverUserId(previewDriverUserId);

  if (resolvedDispatchDate) {
    resolvedUrl.searchParams.set("dispatch_date", resolvedDispatchDate);
  } else {
    resolvedUrl.searchParams.delete("dispatch_date");
  }

  if (resolvedDispatchPeriod) {
    resolvedUrl.searchParams.set("dispatch_period", resolvedDispatchPeriod);
  } else {
    resolvedUrl.searchParams.delete("dispatch_period");
  }

  if (resolvedPreviewDriverUserId) {
    resolvedUrl.searchParams.set("preview_driver_user_id", resolvedPreviewDriverUserId);
  } else {
    resolvedUrl.searchParams.delete("preview_driver_user_id");
  }

  return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

export function appendDriverDispatchDate(path, dispatchDate = "") {
  return appendDriverPortalSearchParams(path, { dispatchDate });
}

export function buildDriverPortalHomePath(dispatchDateOrOptions = "", previewDriverUserId = "") {
  const options = resolveDriverPortalPathOptions(dispatchDateOrOptions, previewDriverUserId);
  return appendDriverPortalSearchParams(DRIVER_PORTAL_HOME_PATH, options);
}

export function buildDriverPortalDeliveryPath(deliveryId, dispatchDateOrOptions = "", previewDriverUserId = "") {
  const encodedDeliveryId = encodeURIComponent(String(deliveryId || "").trim());
  const options = resolveDriverPortalPathOptions(dispatchDateOrOptions, previewDriverUserId);
  return appendDriverPortalSearchParams(`${DRIVER_PORTAL_DELIVERY_PREFIX}/${encodedDeliveryId}`, options);
}

function resolveApiErrorMessage(payload, fallbackMessage) {
  const publicationUnavailability = normalizeDriverPortalPublicationUnavailability(payload);
  if (publicationUnavailability?.message) {
    return publicationUnavailability.message;
  }

  // Le chauffeur ne peut pas rouvrir une journée : il n'a pas à lire la consigne du répartiteur.
  if (["cloture", "livre"].includes(payload?.dispatch_status)) {
    return "Cette journée est fermée. Au besoin, contactez votre répartiteur.";
  }

  if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  if (payload?.detail && typeof payload.detail.message === "string" && payload.detail.message.trim()) {
    return payload.detail.message.trim();
  }

  if (payload && typeof payload.error === "string" && payload.error.trim()) {
    return payload.error.trim();
  }

  if (payload && typeof payload.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  return fallbackMessage;
}

async function requestJson(path, {
  acceptableStatusCodes = [],
  ...init
} = {}) {
  const response = await fetchWithSupabaseAuth(path, {
    cache: "no-store",
    ...init,
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok && !acceptableStatusCodes.includes(response.status)) {
    const requestError = new Error(resolveApiErrorMessage(
      payload,
      `Erreur ${response.status} : ${response.statusText || "Requête impossible."}`,
    ));
    requestError.status = response.status;
    requestError.payload = payload;
    requestError.publicationUnavailability = normalizeDriverPortalPublicationUnavailability(payload);
    throw requestError;
  }

  return {
    payload,
    response,
  };
}

export function hasDispatcherWorkspaceAccess(accessState) {
  if (typeof accessState?.dispatcherWorkspaceAccess === "boolean") {
    return accessState.dispatcherWorkspaceAccess;
  }

  if (accessState?.accountType === "driver_portal") {
    return false;
  }

  const normalizedPermissions = Array.isArray(accessState?.permissions)
    ? accessState.permissions.filter(Boolean)
    : [];
  if (
    normalizedPermissions.length > 0
    && normalizedPermissions.every((permissionKey) => permissionKey === ACCESS_PERMISSION_KEYS.driverPortalAccess)
  ) {
    return false;
  }

  return Boolean(accessState?.activeRole)
    || (Array.isArray(accessState?.permissions) && accessState.permissions.length > 0);
}

function hasResolvedAuthenticatedEntryAccess(accessState) {
  if (!accessState || typeof accessState !== "object") {
    return false;
  }

  if (typeof accessState.dispatcherWorkspaceAccess === "boolean") {
    return true;
  }

  if (typeof accessState.driverPortalAccess === "boolean") {
    return true;
  }

  if (typeof accessState.accountType === "string" && accessState.accountType.trim()) {
    return true;
  }

  if (typeof accessState.activeRole === "string" && accessState.activeRole.trim()) {
    return true;
  }

  if (typeof accessState.source === "string" && accessState.source.trim()) {
    return true;
  }

  if (typeof accessState.userId === "string" && accessState.userId.trim()) {
    return true;
  }

  return Array.isArray(accessState.permissions) && accessState.permissions.length > 0;
}

function hasDriverPortalOnlyAccess(accessState) {
  if (accessState?.accountType === "driver_portal") {
    return true;
  }

  if (hasDispatcherWorkspaceAccess(accessState)) {
    return false;
  }

  if (typeof accessState?.driverPortalAccess === "boolean") {
    return accessState.driverPortalAccess;
  }

  const normalizedPermissions = Array.isArray(accessState?.permissions)
    ? accessState.permissions.filter(Boolean)
    : [];

  return normalizedPermissions.length > 0
    && normalizedPermissions.every((permissionKey) => permissionKey === ACCESS_PERMISSION_KEYS.driverPortalAccess);
}

export async function probeDriverPortalAvailability() {
  const { response } = await requestJson("/api/driver/me/deliveries", {
    acceptableStatusCodes: [401, 403, 404],
  });

  return response.ok;
}

// Cache module-level pour éviter un appel réseau /api/me/access à chaque navigation
// dans le shell protégé. L'accès ne change pas en cours de session.
let _cachedEntryPath = null;
let _cachedEntryPathAt = 0;
const ENTRY_PATH_CACHE_MS = 60000; // 60 secondes

function _readCachedEntryPath() {
  if (_cachedEntryPath && Date.now() - _cachedEntryPathAt < ENTRY_PATH_CACHE_MS) {
    return _cachedEntryPath;
  }
  return null;
}

function _writeCachedEntryPath(entryPath) {
  _cachedEntryPath = entryPath;
  _cachedEntryPathAt = Date.now();
}

export function clearCachedEntryPath() {
  _cachedEntryPath = null;
  _cachedEntryPathAt = 0;
}

export async function resolveAuthenticatedEntryPath({
  accessState = null,
  fallbackPath = OPERATOR_HOME_PATH,
} = {}) {
  const hasExplicitAccessState = accessState !== null && accessState !== undefined;
  let resolvedAccessState = hasResolvedAuthenticatedEntryAccess(accessState) ? accessState : null;

  if (!resolvedAccessState) {
    // Évite un appel réseau bloquant si l'accès a déjà été résolu récemment.
    if (!hasExplicitAccessState) {
      const cachedPath = _readCachedEntryPath();
      if (cachedPath) {
        return cachedPath;
      }
    }

    const { payload } = await requestJson("/api/me/access");
    writeAccessSessionCache(payload);
    resolvedAccessState = buildAccessState(payload);
  }

  let entryPath;
  if (isBillingWorkspaceOnly(resolvedAccessState)) {
    entryPath = resolveBillingWorkspaceEntryPath(resolvedAccessState) || OPERATOR_HOME_PATH;
  } else if (hasDispatcherWorkspaceAccess(resolvedAccessState)) {
    entryPath = OPERATOR_HOME_PATH;
  } else if (hasDriverPortalOnlyAccess(resolvedAccessState)) {
    entryPath = DRIVER_PORTAL_HOME_PATH;
  } else {
    entryPath = fallbackPath;
  }

  // Cache le résultat pour les navigations suivantes dans la même session.
  _writeCachedEntryPath(entryPath);
  return entryPath;
}

export async function loadDriverDeliveries({ dispatchDate = "", dispatchPeriod = "", previewDriverUserId = "" } = {}) {
  const { payload } = await requestJson(appendDriverPortalSearchParams("/api/driver/me/deliveries", {
    dispatchDate,
    dispatchPeriod,
    previewDriverUserId,
  }));
  return payload;
}

export async function loadDriverDispatchDayContext({ dispatchDate = "", dispatchPeriod = "", previewDriverUserId = "" } = {}) {
  const requestPath = appendDriverPortalSearchParams("/api/driver/me/dispatch-days", {
    dispatchDate,
    dispatchPeriod,
    previewDriverUserId,
  });
  const { payload } = await requestJson(requestPath);
  return payload;
}

export async function loadDriverDelivery(deliveryId, { dispatchDate = "", dispatchPeriod = "", previewDriverUserId = "" } = {}) {
  const encodedDeliveryId = encodeURIComponent(String(deliveryId || "").trim());
  const { payload } = await requestJson(
    appendDriverPortalSearchParams(`/api/driver/me/deliveries/${encodedDeliveryId}`, {
      dispatchDate,
      dispatchPeriod,
      previewDriverUserId,
    }),
  );
  return payload;
}

export async function confirmDriverDelivery(deliveryId, nextPayload, { dispatchDate = "", dispatchPeriod = "", previewDriverUserId = "" } = {}) {
  const encodedDeliveryId = encodeURIComponent(String(deliveryId || "").trim());
  const { payload } = await requestJson(appendDriverPortalSearchParams(`/api/driver/me/deliveries/${encodedDeliveryId}/confirm`, {
    dispatchDate,
    dispatchPeriod,
    previewDriverUserId,
  }), {
    body: JSON.stringify(nextPayload || {}),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  return payload;
}

export async function unconfirmDriverDelivery(deliveryId, {
  dispatchDate = "",
  dispatchPeriod = "",
  previewDriverUserId = "",
  publicationRevision = null,
  expectedExecutionRevision = "",
} = {}) {
  const encodedDeliveryId = encodeURIComponent(String(deliveryId || "").trim());
  const { payload } = await requestJson(appendDriverPortalSearchParams(`/api/driver/me/deliveries/${encodedDeliveryId}/unconfirm`, {
    dispatchDate,
    dispatchPeriod,
    previewDriverUserId,
  }), {
    body: JSON.stringify({
      publication_revision: normalizePublicationRevision(publicationRevision),
      expected_execution_revision: normalizeInlineText(expectedExecutionRevision) || null,
    }),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  return payload;
}

export async function changeDriverDeparture(nextPayload, options = {}) {
  const { payload } = await requestJson(appendDriverPortalSearchParams("/api/driver/me/departures", options), {
    body: JSON.stringify(nextPayload), headers: { "Content-Type": "application/json" }, method: "POST",
  });
  return payload;
}

export async function confirmDriverLoading(loadingStepId, nextPayload, { dispatchDate = "", dispatchPeriod = "", previewDriverUserId = "" } = {}) {
  const encodedLoadingStepId = encodeURIComponent(String(loadingStepId || "").trim());
  const { payload } = await requestJson(appendDriverPortalSearchParams(`/api/driver/me/loadings/${encodedLoadingStepId}/confirm`, {
    dispatchDate,
    dispatchPeriod,
    previewDriverUserId,
  }), {
    body: JSON.stringify(nextPayload || {}),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  return payload;
}

export async function unconfirmDriverLoading(loadingStepId, {
  dispatchDate = "",
  dispatchPeriod = "",
  previewDriverUserId = "",
  publicationRevision = null,
  expectedExecutionRevision = "",
} = {}) {
  const encodedLoadingStepId = encodeURIComponent(String(loadingStepId || "").trim());
  const { payload } = await requestJson(appendDriverPortalSearchParams(`/api/driver/me/loadings/${encodedLoadingStepId}/unconfirm`, {
    dispatchDate,
    dispatchPeriod,
    previewDriverUserId,
  }), {
    body: JSON.stringify({
      publication_revision: normalizePublicationRevision(publicationRevision),
      expected_execution_revision: normalizeInlineText(expectedExecutionRevision) || null,
    }),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  return payload;
}

export async function resetDriverConfirmations({
  dispatchDate = "",
  dispatchPeriod = "",
  previewDriverUserId = "",
  publicationRevisions = {},
  deliveryExecutionRevisions = {},
  loadingExecutionRevisions = {},
} = {}) {
  const { payload } = await requestJson(appendDriverPortalSearchParams("/api/driver/me/confirmations/reset", {
    dispatchDate,
    dispatchPeriod,
    previewDriverUserId,
  }), {
    body: JSON.stringify({
      publication_revisions: Object.fromEntries(
        Object.entries(publicationRevisions && typeof publicationRevisions === "object" ? publicationRevisions : {})
          .map(([truckId, revision]) => [String(truckId || "").trim(), normalizePublicationRevision(revision)])
          .filter(([truckId, revision]) => Boolean(truckId) && revision !== null),
      ),
      delivery_execution_revisions: Object.fromEntries(
        Object.entries(deliveryExecutionRevisions && typeof deliveryExecutionRevisions === "object" ? deliveryExecutionRevisions : {})
          .map(([deliveryId, revision]) => [normalizeInlineText(deliveryId), normalizeInlineText(revision)])
          .filter(([deliveryId, revision]) => Boolean(deliveryId) && Boolean(revision)),
      ),
      loading_execution_revisions: Object.fromEntries(
        Object.entries(loadingExecutionRevisions && typeof loadingExecutionRevisions === "object" ? loadingExecutionRevisions : {})
          .map(([loadingStepId, revision]) => [normalizeInlineText(loadingStepId), normalizeInlineText(revision)])
          .filter(([loadingStepId, revision]) => Boolean(loadingStepId) && Boolean(revision)),
      ),
    }),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  return payload;
}

export async function recordDriverPortalAuthentication() {
  const { payload } = await requestJson("/api/driver-accounts/authentication", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  return payload;
}

function isDriverPortalInitialPreloadStep(step) {
  return Boolean(
    step
    && step.step_type === "loading"
    && (step.is_initial_preload || step.is_preloaded_previous_day),
  );
}

function resolveDriverPortalCandidateTrip(assignment, tripId = "") {
  if (!assignment || !Array.isArray(assignment?.trips)) {
    return null;
  }

  return assignment.trips.find((trip) => trip?.trip_id && (!tripId || trip.trip_id === tripId)) || null;
}

function resolveDriverPortalStepTripId(step) {
  return step?.trip_id || step?.linked_delivery?.trip_id || "";
}

export function normalizeDriverPortalMapsAddress(value) {
  const address = typeof value === "string" ? value.trim() : "";
  return /^adresse non renseignée$/i.test(address) ? "" : address;
}

export function buildDriverPortalDirectionsUrl(originAddress = "", destinationAddress = "") {
  const origin = normalizeDriverPortalMapsAddress(originAddress);
  const destination = normalizeDriverPortalMapsAddress(destinationAddress);
  if (!destination || (origin && origin.localeCompare(destination, undefined, { sensitivity: "base" }) === 0)) {
    return "";
  }
  const params = new URLSearchParams({ api: "1", destination, travelmode: "driving" });
  if (origin) params.set("origin", origin);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function resolveDriverPortalOriginStop(candidateTrip) {
  const routableStops = Array.isArray(candidateTrip?.routable_stops) ? candidateTrip.routable_stops : [];
  const stops = Array.isArray(candidateTrip?.stops) ? candidateTrip.stops : [];
  const depotStart = routableStops.find((stop) => stop?.stop_type === "depot_start")
    || stops.find((stop) => stop?.stop_type === "depot_start");
  return depotStart || routableStops[0] || stops[0] || null;
}

/**
 * Résout le mode carte et l'index de segment routable pour un step portail.
 * Les préchargements virtuels n'ont pas d'entrée dans routable_stops / segment_geometries.
 * @returns {{ mode: "origin-only" } | { mode: "segment", routeSegmentIndex: number | null }}
 */
export function resolveDriverPortalRouteSegmentIndex(assignmentOrSteps, step) {
  if (isDriverPortalInitialPreloadStep(step)) {
    return { mode: "origin-only" };
  }

  const tripId = resolveDriverPortalStepTripId(step);
  const candidateTrip = Array.isArray(assignmentOrSteps) ? null : resolveDriverPortalCandidateTrip(assignmentOrSteps, tripId);
  const assignmentSteps = Array.isArray(assignmentOrSteps)
    ? assignmentOrSteps
    : Array.isArray(assignmentOrSteps?.operational_steps)
      ? assignmentOrSteps.operational_steps
      : Array.isArray(assignmentOrSteps?.trips)
        ? (candidateTrip?.operational_steps || [])
        : [];
  const scopedSteps = tripId ? assignmentSteps.filter((candidate) => resolveDriverPortalStepTripId(candidate) === tripId) : assignmentSteps;
  const operationalSteps = scopedSteps.length > 0 ? scopedSteps
    : Array.isArray(candidateTrip?.operational_steps) ? candidateTrip.operational_steps
      : candidateTrip && assignmentOrSteps.trips.length === 1 ? assignmentSteps : [];

  let nonPreloadCountBefore = 0;
  let stepMatched = false;
  for (let index = 0; index < operationalSteps.length; index += 1) {
    const candidate = operationalSteps[index];
    if (candidate === step || (
      step?.step_id
      && candidate?.step_id
      && String(candidate.step_id) === String(step.step_id)
    )) {
      stepMatched = true;
      break;
    }
    if (!isDriverPortalInitialPreloadStep(candidate)) {
      nonPreloadCountBefore += 1;
    }
  }

  if (stepMatched) {
    return { mode: "segment", routeSegmentIndex: nonPreloadCountBefore };
  }

  // Fallback legacy: route_segment_index était l'index operational_steps (preloads inclus).
  const rawSegmentIndex = Number(step?.route_segment_index);
  if (!Number.isFinite(rawSegmentIndex) || rawSegmentIndex < 0) {
    return { mode: "segment", routeSegmentIndex: null };
  }

  const totalPreloads = operationalSteps.filter(isDriverPortalInitialPreloadStep).length;
  return {
    mode: "segment",
    routeSegmentIndex: Math.max(0, rawSegmentIndex - totalPreloads),
  };
}

/**
 * Distance du segment routable corrigé (ignore le décalage preload sur route_distance_km stocké).
 */
export function resolveDriverPortalRouteDistanceKm(assignment, step) {
  const resolved = resolveDriverPortalRouteSegmentIndex(assignment, step);
  if (resolved.mode === "origin-only") {
    return null;
  }

  const candidateTrip = resolveDriverPortalCandidateTrip(assignment, resolveDriverPortalStepTripId(step));
  const segmentDistances = Array.isArray(candidateTrip?.route?.segment_distances)
    ? candidateTrip.route.segment_distances
    : [];
  const segmentIndex = resolved.routeSegmentIndex;
  if (segmentIndex != null && segmentIndex >= 0 && segmentIndex < segmentDistances.length) {
    const segment = segmentDistances[segmentIndex];
    const distanceKm = segment?.distance_km != null ? Number(segment.distance_km)
      : segment?.distance_meters != null ? Number(segment.distance_meters) / 1000 : NaN;
    if (Number.isFinite(distanceKm)) {
      return distanceKm;
    }
  }

  const fallbackKm = step?.route_distance_km != null ? Number(step.route_distance_km) : NaN;
  return Number.isFinite(fallbackKm) ? fallbackKm : null;
}

export function resolveDriverPortalRouteOriginAddress(assignment, step) {
  const trip = resolveDriverPortalCandidateTrip(assignment, resolveDriverPortalStepTripId(step));
  const resolved = resolveDriverPortalRouteSegmentIndex(assignment, step);
  if (!trip || resolved.mode === "origin-only") return "";
  const origin = resolved.routeSegmentIndex != null ? trip.routable_stops?.[resolved.routeSegmentIndex] : null;
  return normalizeDriverPortalMapsAddress(origin?.address)
    || (resolved.routeSegmentIndex === 0 ? normalizeDriverPortalMapsAddress(resolveDriverPortalOriginStop(trip)?.address) : "");
}

export function buildDriverPortalOriginOnlyTripFromAssignment(assignment, tripId = "") {
  const candidateTrip = resolveDriverPortalCandidateTrip(assignment, tripId);
  if (!candidateTrip) {
    return null;
  }

  const originStop = resolveDriverPortalOriginStop(candidateTrip);
  if (!originStop) {
    return null;
  }

  const taggedOrigin = { ...originStop, badge_role: "origin" };
  return {
    trip_id: candidateTrip.trip_id,
    routable_stops: [taggedOrigin],
    stops: [taggedOrigin],
    // Pas de spread du route complet: route.geometry dessinerait tout le trajet.
    route: {
      status: "unavailable",
      geometry: null,
      segment_geometries: [],
      geometry_available: false,
      segment_distances_meters: [],
    },
  };
}

export function buildDriverPortalRouteTripFromAssignment(assignment, routeSegmentIndex, tripId = "") {
  const candidateTrip = resolveDriverPortalCandidateTrip(assignment, tripId);
  if (!candidateTrip) {
    return null;
  }

  const segmentIndex = Number.isFinite(routeSegmentIndex) && routeSegmentIndex >= 0 ? routeSegmentIndex : null;
  const segmentGeometries = Array.isArray(candidateTrip.route?.segment_geometries) ? candidateTrip.route.segment_geometries : [];
  const routableStops = Array.isArray(candidateTrip.routable_stops) ? candidateTrip.routable_stops : [];

  const extractSegmentAndStops = () => {
    if (segmentIndex == null || segmentIndex >= segmentGeometries.length || segmentGeometries.length === 0) {
      return null;
    }

    const segmentGeometry = segmentGeometries[segmentIndex];
    const previousStop = routableStops[segmentIndex];
    const currentStop = routableStops[segmentIndex + 1];

    if (!segmentGeometry || !previousStop || !currentStop) {
      return null;
    }

    return { segmentGeometry, previousStop, currentStop };
  };

  const resolved = extractSegmentAndStops();
  if (resolved) {
    return {
      trip_id: candidateTrip.trip_id,
      routable_stops: [
        { ...resolved.previousStop, badge_role: "origin" },
        { ...resolved.currentStop, badge_role: "destination" },
      ],
      stops: [
        { ...resolved.previousStop, badge_role: "origin" },
        { ...resolved.currentStop, badge_role: "destination" },
      ],
      route: {
        ...(candidateTrip.route || {}),
        segment_geometries: [resolved.segmentGeometry],
      },
    };
  }

  // Fallback: index invalide mais le trip complet a une géométrie exploitable
  if (segmentGeometries.length > 0) {
    const taggedRoutableStops = routableStops.length > 0
      ? routableStops.map((stop, stopIndex) => ({
          ...stop,
          badge_role: stopIndex === 0 ? "origin" : stopIndex === routableStops.length - 1 ? "destination" : "",
        }))
      : routableStops;

    const taggedStops = Array.isArray(candidateTrip.stops) && candidateTrip.stops.length > 0
      ? candidateTrip.stops.map((stop, stopIndex) => ({
          ...stop,
          badge_role: stopIndex === 0 ? "origin" : stopIndex === candidateTrip.stops.length - 1 ? "destination" : "",
        }))
      : taggedRoutableStops;

    return {
      trip_id: candidateTrip.trip_id,
      routable_stops: taggedRoutableStops,
      stops: taggedStops,
      route: candidateTrip.route || {},
    };
  }

  return null;
}
